import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, Play, ExternalLink, Calendar, ListPlus, Package, Smartphone, ChevronDown, X, Loader2, Plus, Copy } from 'lucide-react';
import api, { executionPlanApi, categoriesApi } from '../services/api';
import DuplicateGoalModal from '../components/DuplicateGoalModal';
import AdvancedSettings from '../components/AdvancedSettings';
import Dialog from '../components/Dialog';
import ScheduleModal from '../components/ScheduleModal';
import { useDialog } from '../hooks/useDialog';
import { useWorkspace } from '../context/WorkspaceContext';
import DevicePicker from '../components/DevicePicker';
import PremiumBadge from '../components/PremiumBadge';
import './GoalDetail.css';

const GoalDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { dialogState, closeDialog, confirm, alert } = useDialog();

    const [goal, setGoal] = useState(null);
    const [executions, setExecutions] = useState([]);
    const [linkedIssues, setLinkedIssues] = useState([]);
    const [app, setApp] = useState(null);
    const [loading, setLoading] = useState(true);

    // Workspace & Device selection states
    const { currentWorkspace } = useWorkspace();
    const realDevicesEnabled = currentWorkspace?.subscription?.quotas?.realDevicesEnabled === true;

    const [selectedApp, setSelectedApp] = useState(null);
    const [showAppPicker, setShowAppPicker] = useState(false);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [showDevicePicker, setShowDevicePicker] = useState(false);
    const [apps, setApps] = useState([]);
    const [appsLoading, setAppsLoading] = useState(false);

    // Edit modal state
    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState({
        goalText: '',
        title: '',
        turbo: false,
        strict: true,
        geolocation: ''
    });

    const [workspaceCategories, setWorkspaceCategories] = useState([]);
    const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // Schedule modal state
    const [showScheduleModal, setShowScheduleModal] = useState(false);

    // Add to Plan state
    const [showPlanModal, setShowPlanModal] = useState(false);
    const [plans, setPlans] = useState([]);
    
    // Duplicate modal state
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [selectedPlanId, setSelectedPlanId] = useState('');
    const [isAddingToPlan, setIsAddingToPlan] = useState(false);

    useEffect(() => {
        fetchGoalDetails();
    }, [id]);

    const fetchGoalDetails = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/tests/goals/${id}`);

            setGoal(response.data.goal);
            setExecutions(response.data.executions);
            setLinkedIssues(response.data.linkedIssues);
            setApp(response.data.app || null);
            if (response.data.goal?.categories) {
                setSelectedCategoryIds(response.data.goal.categories.map(c => c.id));
            }
        } catch (error) {
            console.error('Error fetching goal details:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = useCallback(async () => {
        if (!currentWorkspace?.id) return;
        try {
            const response = await categoriesApi.list(currentWorkspace.id);
            setWorkspaceCategories(response.data.categories || []);
        } catch (err) {
            console.error('Error loading workspace categories:', err);
        }
    }, [currentWorkspace?.id]);

    useEffect(() => {
        loadCategories();
    }, [loadCategories]);

    const fetchPlans = async () => {
        try {
            const response = await executionPlanApi.getExecutionPlans();
            const plansData = response.data.rows || response.data.executionPlans || response.data;
            setPlans(Array.isArray(plansData) ? plansData : []);
        } catch (error) {
            console.error('Error fetching plans:', error);
        }
    };

    const loadApps = useCallback(async () => {
        if (!currentWorkspace?.id) return;

        setAppsLoading(true);
        try {
            const response = await api.get('/apps', {
                params: {
                    workspaceId: currentWorkspace.id,
                    limit: 50
                }
            });
            setApps(response.data.rows || []);
        } catch (err) {
            console.error('Failed to load apps:', err);
        } finally {
            setAppsLoading(false);
        }
    }, [currentWorkspace?.id]);

    const handleAddToPlan = async () => {
        if (!selectedPlanId) return;
        setIsAddingToPlan(true);
        try {
            await executionPlanApi.addGoalToPlan(selectedPlanId, id);
            setShowPlanModal(false);
            await alert({
                title: 'Success',
                message: 'Goal added to execution plan successfully!',
                variant: 'success'
            });
        } catch (error) {
            setShowPlanModal(false);
            console.error('Error adding goal to plan:', error);
            await alert({
                title: 'Error',
                message: error.response?.data?.error || 'Failed to add goal to execution plan.',
                variant: 'danger'
            });
        } finally {
            setIsAddingToPlan(false);
        }
    };

    const [isRerunning, setIsRerunning] = useState(false);

    const handleRerun = async () => {
        try {
            setIsRerunning(true);
            const response = await api.post(`/tests/goals/${id}/rerun`);
            // Redirect to live execution page
            window.location.href = `/test/execution/${response.data.execution.id}`;
        } catch (error) {
            console.error('Error rerunning goal:', error);
            const errorMessage = error.response?.data?.error || 'Failed to rerun test goal.'
            let displayMessage = errorMessage;
            if (errorMessage.toLowerCase().includes('quota exceeded')) {
                displayMessage = (
                    <span>
                        {errorMessage}.{' '}
                        <Link to="/settings/billing" style={{ textDecoration: 'underline' }}>
                            Upgrade
                        </Link>
                    </span>
                );
            }
            await alert({
                title: 'Error',
                message: displayMessage,
                variant: 'danger'
            });
        } finally {
            setIsRerunning(false);
        }
    };

    const handleDelete = async () => {
        const confirmed = await confirm({
            title: 'Delete Test Goal',
            message: 'Are you sure you want to delete this test goal? This action cannot be undone.',
            confirmText: 'Delete',
            cancelText: 'Cancel',
            variant: 'danger'
        });

        if (!confirmed) return;

        try {
            await api.delete(`/tests/goals/${id}`);
            // Redirect back to goals list
            window.location.href = '/goals';
        } catch (error) {
            console.error('Error deleting goal:', error);
            await alert({
                title: 'Error',
                message: 'Failed to delete test goal. Please try again.',
                variant: 'danger'
            });
        }
    };

    const handleEdit = () => {
        setEditForm({
            goalText: goal.goalText,
            title: goal.title || '',
            turbo: goal.appConfig?.turbo || false,
            strict: goal.appConfig?.strict !== false,
            geolocation: goal.appConfig?.geolocation || ''
        });
        setSelectedCategoryIds(goal.categories ? goal.categories.map(c => c.id) : []);
        setSelectedApp(app);
        setSelectedDevice({
            arn: goal.deviceArn || (goal.deviceProvider === 'emulator' ? 'emulator' : 'auto'),
            provider: goal.deviceProvider || 'emulator',
            platform: goal.platform,
            name: goal.deviceArn === 'emulator'
                ? 'Android Emulator (Standard)'
                : (goal.deviceArn === 'auto' ? 'Auto (pick best device)' : (goal.deviceArn || 'Select Device'))
        });
        loadApps();
        setShowEditModal(true);
    };

    const handleAppSelect = (appItem) => {
        setSelectedApp(appItem);
        const appPlatform = appItem.platform.toLowerCase();
        if (appPlatform === 'android' && !realDevicesEnabled) {
            setSelectedDevice({
                arn: 'emulator',
                name: 'Android Emulator (Standard)',
                model: 'Virtual Emulator',
                os: 'Android 14 (Standard)',
                platform: 'android',
                availability: 'AVAILABLE',
                provider: 'emulator'
            });
        } else {
            setSelectedDevice(null);
        }
        setShowAppPicker(false);
    };

    const handleDeviceSelect = (deviceItem) => {
        setSelectedDevice(deviceItem);
        setShowDevicePicker(false);
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        const mb = bytes / (1024 * 1024);
        return `${mb.toFixed(1)} MB`;
    };

    const getPlatformIcon = (platform) => {
        return platform === 'android' ? '🤖' : '🍎';
    };

    const handleSaveEdit = async () => {
        try {
            if (!selectedApp) {
                await alert({
                    title: 'Error',
                    message: 'Please select an app to test',
                    variant: 'danger'
                });
                return;
            }
            if (!selectedDevice) {
                await alert({
                    title: 'Error',
                    message: 'Please select a device to run the test',
                    variant: 'danger'
                });
                return;
            }

            await api.put(`/tests/goals/${id}`, {
                goalText: editForm.goalText,
                platform: selectedApp.platform.toLowerCase(),
                deviceProvider: selectedDevice.provider || 'aws',
                deviceArn: selectedDevice.arn,
                appConfig: { 
                    appId: selectedApp.id,
                    turbo: editForm.turbo,
                    strict: editForm.strict,
                    geolocation: editForm.geolocation || null
                },
                title: editForm.title || undefined,
                categoryIds: selectedCategoryIds
            });

            setShowEditModal(false);
            await alert({
                title: 'Success',
                message: 'Test goal updated successfully!',
                variant: 'success'
            });
            fetchGoalDetails(); // Reload goal data
        } catch (error) {
            console.error('Error updating goal:', error);
            await alert({
                title: 'Error',
                message: error.response?.data?.message || error.response?.data?.error || 'Failed to update test goal. Please try again.',
                variant: 'danger'
            });
        }
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDuration = (ms) => {
        if (!ms) return 'N/A';
        const seconds = Math.floor(ms / 1000);
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}m ${remainingSeconds}s`;
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            success: { label: '✓ Pass', className: 'status-success' },
            failed: { label: '✗ Fail', className: 'status-failed' },
            error: { label: '⚠ Error', className: 'status-error' },
            running: { label: '⟳ Running', className: 'status-running' },
            queued: { label: '⋯ Queued', className: 'status-queued' },
            inconclusive: { label: '? Inconclusive', className: 'status-inconclusive' }
        };

        const config = statusMap[status] || { label: status, className: 'status-unknown' };
        return <span className={`status-badge ${config.className}`}>{config.label}</span>;
    };

    if (loading) {
        return <div className="loading-container">Loading goal details...</div>;
    }

    if (!goal) {
        return <div className="error-container">Goal not found</div>;
    }

    return (
        <div className="goal-detail-container">
            <div className="goal-detail-header">
                <Link to="/goals" className="btn btn-ghost btn-sm">
                    <ArrowLeft size={16} />
                    Back to Goals
                </Link>
            </div>

            <div className="goal-info-card">
                <div className="goal-info-header">
                    <div>
                        <h1>{goal.title || goal.goalText}</h1>
                        {goal.title && (
                            <div style={{ marginTop: '8px', fontSize: '15px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>{goal.goalText}</div>
                        )}
                        {goal.categories && goal.categories.length > 0 && (
                            <div className="goal-detail-categories" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
                                {goal.categories.map(cat => (
                                    <span
                                        key={cat.id}
                                        className="category-tag-badge"
                                        style={{
                                            backgroundColor: cat.color ? `${cat.color}20` : '#94a3b820',
                                            color: cat.color || '#94a3b8',
                                            border: `1px solid ${cat.color || '#94a3b8'}40`,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            padding: '4px 10px',
                                            borderRadius: '20px',
                                            fontSize: '12px',
                                            fontWeight: 600
                                        }}
                                    >
                                        {cat.name}
                                    </span>
                                ))}
                            </div>
                        )}
                        <div className="goal-meta" style={{ marginTop: '12px' }}>
                            <span>Created {formatDate(goal.createdAt)}</span>
                            {executions.length > 0 && (
                                <>
                                    <span className="meta-divider">•</span>
                                    <span>Last run {formatDate(executions[0].createdAt)}</span>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="goal-actions">
                        <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => {
                                fetchPlans();
                                setShowPlanModal(true);
                            }}
                        >
                            <ListPlus size={16} />
                            Add to Plan
                        </button>
                        <button
                            className="btn btn-secondary btn-sm"
                            onClick={handleRerun}
                            disabled={isRerunning}
                        >
                            <Play size={16} />
                            {isRerunning ? 'Running...' : 'Rerun'}
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={handleEdit}>
                            <Edit size={16} />
                            Edit
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={() => setShowDuplicateModal(true)}>
                            <Copy size={16} />
                            Duplicate
                        </button>
                        <button className="btn btn-ghost btn-sm btn-danger" onClick={handleDelete}>
                            <Trash2 size={16} />
                            Delete
                        </button>
                    </div>
                </div>
            </div>

            <div className="goal-content">
                <div className="section">
                    <h2>Recent Executions ({executions.length})</h2>
                    {executions.length === 0 ? (
                        <div className="empty-state">
                            <p>No executions yet</p>
                            <button
                                className="btn btn-primary"
                                onClick={handleRerun}
                                disabled={isRerunning}>
                                {isRerunning ? 'Running...' : 'Run First Test'}
                            </button>
                        </div>
                    ) : (
                        <div className="executions-table">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Status</th>
                                        <th>Duration</th>
                                        <th>Steps</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {executions.map((execution) => (
                                        <tr key={execution.id} className="execution-row">
                                            <td>{formatDate(execution.createdAt)}</td>
                                            <td>{getStatusBadge(execution.status)}</td>
                                            <td>{formatDuration(execution.duration)}</td>
                                            <td>{execution.executedSteps?.length || 0}</td>
                                            <td>
                                                <Link
                                                    to={`/test/results/${execution.id}`}
                                                    className="btn btn-ghost btn-sm"
                                                >
                                                    View
                                                    <ExternalLink size={14} />
                                                </Link>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {linkedIssues.length > 0 && (
                    <div className="section">
                        <h2>Linked Issues ({linkedIssues.length})</h2>
                        <div className="linked-issues">
                            {linkedIssues.map((issue) => (
                                <div key={issue.id} className="issue-card">
                                    <div className="issue-icon">🔗</div>
                                    <div className="issue-info">
                                        <div className="issue-title">{issue.linearIssueTitle}</div>
                                        <div className="issue-meta">
                                            Linear: {issue.linearIssueId} • {issue.state}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="section">
                    <h2>Configuration</h2>
                    <div className="config-grid">
                        <div className="config-item">
                            <div className="config-label">Platform</div>
                            <div className="config-value">
                                <span className={`platform-badge platform-${goal.platform}`}>
                                    {goal.platform === 'ios' ? '🍎 iOS' : '🤖 Android'}
                                </span>
                            </div>
                        </div>
                        <div className="config-item">
                            <div className="config-label">App</div>
                            <div className="config-value">{app?.name || goal.appConfig?.appName || 'N/A'}</div>
                        </div>
                        <div className="config-item">
                            <div className="config-label">Device Provider</div>
                            <div className="config-value" style={{ textTransform: 'uppercase' }}>{goal.deviceProvider || 'emulator'}</div>
                        </div>
                        {goal.deviceArn && (
                            <div className="config-item">
                                <div className="config-label">Device</div>
                                <div className="config-value">{goal.deviceArn === 'emulator' ? 'Android Emulator (Standard)' : (goal.deviceArn === 'auto' ? 'Auto (pick best device)' : goal.deviceArn)}</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Edit Modal */}
            {showEditModal && (
                <div className="dialog-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="dialog-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                        <div className="dialog-header">
                            <h3 className="dialog-title">Edit Test Goal</h3>
                            <button className="dialog-close" onClick={() => setShowEditModal(false)}>
                                ×
                            </button>
                        </div>

                        <div className="dialog-body">
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
                                {/* Metadata Row: Title and Categories */}
                                <div className="goal-meta-row" style={{ display: 'flex', gap: '16px', width: '100%', marginBottom: '8px' }}>
                                    {/* Goal Title (Optional) */}
                                    <div className="form-group flex-1" style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                                        <label className="form-label" style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: 'var(--text-secondary)' }}>Goal Title (Optional)</label>
                                        <input
                                            type="text"
                                            className="goal-title-input"
                                            placeholder="Give your test goal a short descriptive title..."
                                            value={editForm.title || ''}
                                            onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                                            style={{
                                                width: '100%',
                                                height: '46px',
                                                padding: '12px 14px',
                                                borderRadius: '8px',
                                                border: '1px solid var(--border)',
                                                backgroundColor: 'var(--bg-input, #1a2236)',
                                                color: 'var(--text-primary)',
                                                fontSize: '14px',
                                                outline: 'none'
                                            }}
                                        />
                                    </div>

                                    {/* Categories Autocomplete Select */}
                                    <div className="form-group flex-1" style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                                        <label className="form-label" style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: 'var(--text-secondary)' }}>Categories</label>
                                        <div className="tags-input-container" style={{ position: 'relative', width: '100%' }}>
                                            <div className="tags-input-selected" onClick={() => document.querySelector('.edit-tags-search-input')?.focus()} style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                alignItems: 'center',
                                                gap: '6px',
                                                width: '100%',
                                                padding: '8px 12px',
                                                minHeight: '46px',
                                                border: '1px solid var(--border)',
                                                borderRadius: '8px',
                                                backgroundColor: 'var(--bg-input, #1a2236)',
                                                cursor: 'text'
                                            }}>
                                                {selectedCategoryIds.map(id => {
                                                    const cat = workspaceCategories.find(c => c.id === id);
                                                    if (!cat) return null;
                                                    return (
                                                        <span
                                                            key={id}
                                                            className="category-tag-badge"
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '6px',
                                                                padding: '4px 10px',
                                                                borderRadius: '20px',
                                                                fontSize: '12px',
                                                                fontWeight: 600,
                                                                backgroundColor: cat.color ? `${cat.color}20` : '#94a3b820',
                                                                color: cat.color || '#94a3b8',
                                                                border: `1px solid ${cat.color || '#94a3b8'}40`
                                                            }}
                                                        >
                                                            {cat.name}
                                                            <X
                                                                size={12}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedCategoryIds(prev => prev.filter(cId => cId !== id));
                                                                }}
                                                                style={{ cursor: 'pointer' }}
                                                            />
                                                        </span>
                                                    );
                                                })}
                                                <input
                                                    type="text"
                                                    className="tags-search-input edit-tags-search-input"
                                                    placeholder={selectedCategoryIds.length === 0 ? "Select or create categories..." : ""}
                                                    value={searchQuery}
                                                    onFocus={() => setIsDropdownOpen(true)}
                                                    onBlur={() => setTimeout(() => setIsDropdownOpen(false), 200)}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                    onKeyDown={async (e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            const trimmed = searchQuery.trim();
                                                            if (!trimmed) return;

                                                            const matched = workspaceCategories.find(c => c.name.toLowerCase() === trimmed.toLowerCase());
                                                            if (matched) {
                                                                if (!selectedCategoryIds.includes(matched.id)) {
                                                                    setSelectedCategoryIds(prev => [...prev, matched.id]);
                                                                }
                                                                setSearchQuery('');
                                                                return;
                                                            }

                                                            try {
                                                                const response = await categoriesApi.create({
                                                                    name: trimmed,
                                                                    color: '#' + Math.floor(Math.random()*16777215).toString(16).padStart(6, '0')
                                                                });
                                                                const newCat = response.data.category;
                                                                setWorkspaceCategories(prev => [...prev, newCat]);
                                                                setSelectedCategoryIds(prev => [...prev, newCat.id]);
                                                                setSearchQuery('');
                                                            } catch (err) {
                                                                console.error(err);
                                                            }
                                                        }
                                                    }}
                                                    style={{
                                                        flex: 1,
                                                        minWidth: '120px',
                                                        border: 'none',
                                                        background: 'transparent',
                                                        color: 'var(--text-primary)',
                                                        fontSize: '14px',
                                                        outline: 'none',
                                                        padding: '2px 0'
                                                    }}
                                                />
                                            </div>

                                            {isDropdownOpen && (
                                                <div className="tags-dropdown-menu" style={{
                                                    position: 'absolute',
                                                    top: 'calc(100% + 6px)',
                                                    left: 0,
                                                    right: 0,
                                                    maxHeight: '200px',
                                                    overflowY: 'auto',
                                                    background: 'rgba(21, 28, 44, 0.95)',
                                                    backdropFilter: 'blur(12px)',
                                                    border: '1px solid var(--border-light, #334155)',
                                                    borderRadius: '8px',
                                                    boxShadow: 'var(--shadow-lg)',
                                                    zIndex: 100
                                                }}>
                                                    {workspaceCategories
                                                        .filter(cat => !selectedCategoryIds.includes(cat.id) && cat.name.toLowerCase().includes(searchQuery.toLowerCase()))
                                                        .map(cat => (
                                                            <div
                                                                key={cat.id}
                                                                className="tags-dropdown-item"
                                                                onMouseDown={() => {
                                                                    setSelectedCategoryIds(prev => [...prev, cat.id]);
                                                                    setSearchQuery('');
                                                                }}
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    padding: '10px 14px',
                                                                    fontSize: '13px',
                                                                    color: 'var(--text-primary)',
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                <span
                                                                    style={{
                                                                        display: 'inline-block',
                                                                        width: '8px',
                                                                        height: '8px',
                                                                        borderRadius: '50%',
                                                                        backgroundColor: cat.color || '#94a3b8',
                                                                        marginRight: '8px'
                                                                    }}
                                                                />
                                                                {cat.name}
                                                            </div>
                                                        ))
                                                    }
                                                    {searchQuery.trim() && !workspaceCategories.some(c => c.name.toLowerCase() === searchQuery.trim().toLowerCase()) && (
                                                        <div
                                                            className="tags-dropdown-item create-option"
                                                            onMouseDown={async () => {
                                                                const trimmed = searchQuery.trim();
                                                                try {
                                                                    const response = await categoriesApi.create({
                                                                        name: trimmed,
                                                                        color: '#' + Math.floor(Math.random()*16777215).toString(16).padStart(6, '0')
                                                                    });
                                                                    const newCat = response.data.category;
                                                                    setWorkspaceCategories(prev => [...prev, newCat]);
                                                                    setSelectedCategoryIds(prev => [...prev, newCat.id]);
                                                                    setSearchQuery('');
                                                                } catch (err) {
                                                                    console.error(err);
                                                                }
                                                            }}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                padding: '10px 14px',
                                                                fontSize: '13px',
                                                                color: 'var(--primary)',
                                                                cursor: 'pointer',
                                                                borderTop: '1px solid var(--border)',
                                                                fontWeight: 500
                                                            }}
                                                        >
                                                            <Plus size={12} style={{ marginRight: '6px' }} />
                                                            Create category: <strong>"{searchQuery.trim()}"</strong>
                                                        </div>
                                                    )}
                                                    {workspaceCategories.filter(cat => !selectedCategoryIds.includes(cat.id) && cat.name.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 && !searchQuery.trim() && (
                                                        <div className="tags-dropdown-no-results" style={{
                                                            padding: '12px 14px',
                                                            fontSize: '12px',
                                                            color: 'var(--text-muted)',
                                                            textAlign: 'center'
                                                        }}>
                                                            No other categories. Type to create...
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label style={{ display: 'block', marginBottom: 'var(--spacing-xs)', fontSize: '13px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                                        Goal Description
                                    </label>
                                    <textarea
                                        value={editForm.goalText}
                                        onChange={(e) => setEditForm({ ...editForm, goalText: e.target.value })}
                                        rows={4}
                                        style={{
                                            width: '100%',
                                            padding: 'var(--spacing-sm)',
                                            background: 'var(--bg-tertiary)',
                                            border: '1px solid var(--border)',
                                            borderRadius: 'var(--radius-md)',
                                            color: 'var(--text-primary)',
                                            fontSize: '14px',
                                            resize: 'vertical',
                                            marginBottom: 'var(--spacing-md)'
                                        }}
                                        placeholder="Describe what you want to test..."
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', marginBottom: 'var(--spacing-xs)', fontSize: '13px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                                        App & Device Configuration
                                    </label>
                                    <div className="device-selection">
                                        {/* App Selector */}
                                        <button
                                            className="device-selector-btn"
                                            onClick={() => setShowAppPicker(true)}
                                            type="button"
                                        >
                                            <Package size={14} />
                                            {selectedApp ? (
                                                <span className="selected-device-name">{selectedApp.displayName || selectedApp.name}</span>
                                            ) : (
                                                <span>1. Select App</span>
                                            )}
                                            <ChevronDown size={14} />
                                        </button>

                                        {/* Device Selector */}
                                        <button
                                            className={`device-selector-btn ${!selectedApp || (selectedApp.platform === 'android' && !realDevicesEnabled) ? 'disabled' : ''}`}
                                            onClick={() => {
                                                if (!selectedApp) return;
                                                if (selectedApp.platform === 'android' && !realDevicesEnabled) return;
                                                setShowDevicePicker(true);
                                            }}
                                            disabled={!selectedApp || (selectedApp.platform === 'android' && !realDevicesEnabled)}
                                            title={!selectedApp ? 'Select an app first' : (selectedApp.platform === 'android' && !realDevicesEnabled) ? 'Standard emulator selected. Upgrade to access real devices.' : ''}
                                            type="button"
                                        >
                                            <Smartphone size={14} />
                                            {selectedDevice ? (
                                                <span className="selected-device-name">{selectedDevice.name}</span>
                                            ) : (
                                                <span>2. Select Device</span>
                                            )}
                                            {!realDevicesEnabled && <PremiumBadge title='Upgrade to run tests on real devices' />}
                                            {realDevicesEnabled && <ChevronDown size={14} />}
                                        </button>
                                    </div>

                                    {!realDevicesEnabled && selectedApp && selectedApp.platform === 'android' && (
                                        <div className="emulator-warning-info" style={{ marginTop: '10px', marginBottom: '10px' }}>
                                            ℹ️ Your test will run on <strong>Android Emulator</strong>. <Link to="/settings/billing" style={{ color: 'var(--primary-color)', textDecoration: 'underline', fontWeight: 'bold' }}>Upgrade to Business or Enterprise</Link> to run tests on real devices.
                                        </div>
                                    )}

                                    {/* Selected Items Badges */}
                                    {(selectedDevice || selectedApp) && (
                                        <div className="selection-summary">
                                            {selectedApp && (
                                                <div className="selected-device-badge">
                                                    <Package size={12} />
                                                    <span className="device-platform">{selectedApp.platform}</span>
                                                    {selectedApp.version && (
                                                        <span className="device-os-version">v{selectedApp.version}</span>
                                                    )}
                                                    {selectedApp.fileSize && (
                                                        <span className="device-os-version">{formatFileSize(selectedApp.fileSize)}</span>
                                                    )}
                                                    <button className="remove-btn" onClick={() => setSelectedApp(null)} type="button">
                                                        <X size={12} />
                                                    </button>
                                                </div>
                                            )}
                                            {selectedDevice && (
                                                <div className="selected-device-badge">
                                                    <Smartphone size={12} />
                                                    <span className="device-platform">{selectedDevice.platform}</span>
                                                    {selectedDevice.os && <span className="device-os-version">{selectedDevice.os}</span>}
                                                    {selectedDevice.availability === 'AVAILABLE' ? (
                                                        <span className="availability available">Available</span>
                                                    ) : selectedDevice.availability && (
                                                        <span className="availability busy">Busy</span>
                                                    )}
                                                    <button className="remove-btn" onClick={() => setSelectedDevice(null)} type="button">
                                                        <X size={12} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div style={{ marginTop: '8px' }}>
                                    <AdvancedSettings
                                        values={{
                                            turbo: editForm.turbo,
                                            strict: editForm.strict,
                                            geolocation: editForm.geolocation
                                        }}
                                        onChange={(newVal) => setEditForm({
                                            ...editForm,
                                            turbo: newVal.turbo,
                                            strict: newVal.strict,
                                            geolocation: newVal.geolocation
                                        })}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="dialog-footer">
                            <button
                                className="btn btn-ghost btn-sm"
                                onClick={() => setShowEditModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={handleSaveEdit}
                                disabled={!editForm.goalText.trim() || !selectedApp || !selectedDevice}
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Dialog
                isOpen={dialogState.isOpen}
                onClose={closeDialog}
                title={dialogState.title}
                message={dialogState.message}
                type={dialogState.type}
                variant={dialogState.variant}
                confirmText={dialogState.confirmText}
                cancelText={dialogState.cancelText}
                placeholder={dialogState.placeholder}
                defaultValue={dialogState.defaultValue}
                onConfirm={dialogState.onConfirm}
                onCancel={dialogState.onCancel}
            />

            {showScheduleModal && (
                <ScheduleModal
                    onClose={() => setShowScheduleModal(false)}
                    onComplete={() => {
                        setShowScheduleModal(false);
                        alert({
                            title: 'Success',
                            message: 'Schedule created/updated successfully',
                            variant: 'success'
                        });
                    }}
                    preselectedGoals={[goal]}
                    initialData={null}
                />
            )}

            {/* Add to Plan Modal */}
            {showPlanModal && (
                <div className="dialog-overlay" onClick={() => setShowPlanModal(false)}>
                    <div className="dialog-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
                        <div className="dialog-header">
                            <h3 className="dialog-title">Add Goal to Execution Plan</h3>
                            <button className="dialog-close" onClick={() => setShowPlanModal(false)}>
                                ×
                            </button>
                        </div>

                        <div className="dialog-body">
                            <p style={{ marginBottom: 'var(--spacing-md)', fontSize: '14px', color: 'var(--text-secondary)' }}>
                                Select an execution plan to add this goal to.
                            </p>

                            <div>
                                <label style={{ display: 'block', marginBottom: 'var(--spacing-xs)', fontSize: '13px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                                    Execution Plan
                                </label>
                                <select
                                    value={selectedPlanId}
                                    onChange={(e) => setSelectedPlanId(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: 'var(--spacing-sm)',
                                        background: 'var(--bg-tertiary)',
                                        border: '1px solid var(--border)',
                                        borderRadius: 'var(--radius-md)',
                                        color: 'var(--text-primary)',
                                        fontSize: '14px'
                                    }}
                                >
                                    <option value="">Select a plan...</option>
                                    {plans.map(plan => (
                                        <option key={plan.id} value={plan.id}>
                                            {plan.name} ({plan.platform === 'ios' ? 'iOS' : 'Android'})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="dialog-footer">
                            <button
                                className="btn btn-ghost btn-sm"
                                onClick={() => setShowPlanModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={handleAddToPlan}
                                disabled={!selectedPlanId || isAddingToPlan}
                            >
                                {isAddingToPlan ? 'Adding...' : 'Add to Plan'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Device Picker Modal */}
            {showDevicePicker && selectedApp && (
                <div className="device-picker-overlay" onClick={() => setShowDevicePicker(false)}>
                    <div className="device-picker-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Select Device {!realDevicesEnabled && <PremiumBadge title='Upgrade to run tests on real devices' />}</h3>
                            <button className="close-btn" onClick={() => setShowDevicePicker(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <DevicePicker
                            platform={selectedApp.platform.toUpperCase()}
                            onDeviceSelect={handleDeviceSelect}
                            selectedDeviceArn={selectedDevice?.arn}
                        />
                    </div>
                </div>
            )}

            {/* App Picker Modal */}
            {showAppPicker && (
                <div className="device-picker-overlay" onClick={() => setShowAppPicker(false)}>
                    <div className="device-picker-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Select App to Test</h3>
                            <button className="close-btn" onClick={() => setShowAppPicker(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="app-picker">
                            {appsLoading ? (
                                <div className="app-picker-loading">
                                    <Loader2 size={24} className="spinner" />
                                    <p>Loading apps...</p>
                                </div>
                            ) : apps.length === 0 ? (
                                <div className="app-picker-empty">
                                    <Package size={32} />
                                    <p>No apps uploaded yet</p>
                                    <a href="/apps" className="btn btn-secondary">Upload App</a>
                                </div>
                            ) : (
                                <div className="app-list">
                                    {apps.map(appItem => (
                                        <div
                                            key={appItem.id}
                                            className={`app-item ${selectedApp?.id === appItem.id ? 'selected' : ''}`}
                                            onClick={() => handleAppSelect(appItem)}
                                        >
                                            <div className="app-icon">
                                                {appItem.metadata?.appInfo?.icon ? (
                                                    <img
                                                        src={appItem.metadata.appInfo.icon}
                                                        alt={appItem.displayName || appItem.name}
                                                        className="apps_listing__app-icon"
                                                    />
                                                ) : (
                                                    <div className="app-icon-fallback">
                                                        {getPlatformIcon(appItem.platform)}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="app-info">
                                                <div className="app-name">{appItem.displayName || appItem.name}</div>
                                                <div className="app-details">
                                                    <span className="app-platform">{appItem.platform}</span>
                                                    {appItem.version && <span className="app-version">v{appItem.version}</span>}
                                                    {appItem.packageName && <span className="app-package">{appItem.packageName}</span>}
                                                </div>
                                            </div>
                                            {appItem.fileSize && (
                                                <div className="app-size">{formatFileSize(appItem.fileSize)}</div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <DuplicateGoalModal
                isOpen={showDuplicateModal}
                onClose={() => setShowDuplicateModal(false)}
                onComplete={(newGoal) => {
                    alert({
                        title: 'Success',
                        message: 'Test goal duplicated successfully!',
                        variant: 'success'
                    });
                    navigate(`/goals/${newGoal.id}`);
                }}
                goal={goal}
            />
        </div>
    );
};

export default GoalDetail;
